// ReadingTest.js

import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { isTestUnlocked } from "../utils/subscriptionHelper";
import EngdemyLogo from "../assets/Images/EngdemyUploaded.png";
import "./Listening.css";
import "./reading.css";

const TASKS = {
    1: { name: "Reading Correspondence", questions: 11, timeSec: 660 },
    2: { name: "Reading to Apply a Diagram", questions: 8, timeSec: 540 },
    3: { name: "Reading for Information", questions: 9, timeSec: 600 },
    4: { name: "Reading for Viewpoints", questions: 10, timeSec: 780 },
};

const PART_LABELS = {
    1: "Part 1: Reading Correspondence",
    2: "Part 2: Reading to Apply a Diagram",
    3: "Part 3: Reading for Information",
    4: "Part 4: Reading for Viewpoints"
};

const API_BASE = (process.env.REACT_APP_API_BASE_URL && process.env.REACT_APP_API_BASE_URL.startsWith("http"))
    ? process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '')
    : "https://engdemyeducation.com";

function ReadingTest({ testNumber: propTestNumber, onComplete, isFullTest } = {}) {
    const { testNumber: testNumberParam } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const reduxUser = useSelector((state) => state.auth?.authData);

    const getStoredUser = React.useCallback(() => {
        try {
            if (reduxUser?.result) return reduxUser.result;
            if (reduxUser?.email || reduxUser?._id) return reduxUser;
            const rawUser = localStorage.getItem("user_info");
            if (rawUser) {
                const localUser = JSON.parse(rawUser);
                if (localUser?.result) return localUser.result;
                if (localUser?.email || localUser?._id) return localUser;
            }
            const rawAuth = localStorage.getItem("authData");
            if (rawAuth) {
                const localAuth = JSON.parse(rawAuth);
                if (localAuth?.result) return localAuth.result;
                if (localAuth?.email || localAuth?._id) return localAuth;
            }
        } catch (e) {}
        return null;
    }, [reduxUser]);

    const [currentUser, setCurrentUser] = useState(getStoredUser);
    const initialTestNumber = Number(propTestNumber) || Number(testNumberParam) || 1;
    const [testNumber, setTestNumber] = useState(initialTestNumber);
    const [taskIndex, setTaskIndex] = useState(0);
    const [data, setData] = useState([]);
    const [passage, setPassage] = useState(null);
    const [taskAnswers, setTaskAnswers] = useState({ 1: {}, 2: {}, 3: {}, 4: {} });
    const [submittedTasks, setSubmittedTasks] = useState({});
    const [scoreByTest, setScoreByTest] = useState({});
    const [testCompleted, setTestCompleted] = useState({});
    const [selectedReviewPart, setSelectedReviewPart] = useState(1);
    const [timeLeft, setTimeLeft] = useState(TASKS[1].timeSec);
    const timerRef = useRef(null);

    const isShowingResults = location.state?.showResults;
    const [showInstructions, setShowInstructions] = useState(!isShowingResults);
    const [isSpeaking, setIsSpeaking] = useState(false);

    // Speech Synthesis for Instructions
    const stopSpeech = React.useCallback(() => {
        if (typeof window !== "undefined" && "speechSynthesis" in window) {
            window.speechSynthesis.cancel();
            setIsSpeaking(false);
        }
    }, []);

    const playSpeech = React.useCallback((text) => {
        if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.95;
        utterance.pitch = 1.0;
        utterance.lang = "en-US";

        try {
            const voices = window.speechSynthesis.getVoices();
            const enVoice = voices.find(v => v.lang.startsWith("en-") && (v.name.includes("Natural") || v.name.includes("Google") || v.name.includes("Samantha") || v.name.includes("David") || v.name.includes("Zira")));
            if (enVoice) utterance.voice = enVoice;
        } catch (e) {}

        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);

        window.speechSynthesis.speak(utterance);
    }, []);

    const instructionBullets = [
        "On the official test, once you leave a page, you cannot go back to it to change your answers. However, in this practice test, you are free to review and update your answers.",
        "Watch the timer in the top right corner to make sure that you complete each Reading section before the time expires.",
        "This Reading Test follows the format of the official CELPIP test, though the actual exam may include additional questions for research and development purposes."
    ];

    const toggleSpeech = () => {
        if (isSpeaking) {
            stopSpeech();
        } else {
            playSpeech("Reading Test Instructions. " + instructionBullets.join(" "));
        }
    };

    useEffect(() => {
        if (showInstructions) {
            playSpeech("Reading Test Instructions. " + instructionBullets.join(" "));
        } else {
            stopSpeech();
        }
        return () => stopSpeech();
    }, [showInstructions, playSpeech, stopSpeech]);

    // Sync Redux with localStorage if not in Redux
    useEffect(() => {
        const storedUser = getStoredUser();
        if (storedUser) {
            setCurrentUser(storedUser);
        }
        if (!reduxUser && storedUser) {
            const raw = localStorage.getItem("user_info");
            let dataToDispatch = null;
            try {
                const parsed = raw ? JSON.parse(raw) : null;
                if (parsed?.result) dataToDispatch = parsed;
            } catch (e) {}
            if (!dataToDispatch) {
                dataToDispatch = { result: storedUser, ...storedUser };
            }
            dispatch({ type: "AUTH", data: dataToDispatch });
        }
    }, [reduxUser, dispatch, getStoredUser]);

    useEffect(() => {
        if (propTestNumber) {
            setTestNumber(Number(propTestNumber));
        } else if (testNumberParam) {
            setTestNumber(Number(testNumberParam));
        }
    }, [propTestNumber, testNumberParam]);

    // Restrict access for unpaid users or tests not purchased
    useEffect(() => {
        if (isShowingResults) return; // Always allow viewing past results
        if (!isTestUnlocked(testNumber)) {
            alert("This test is locked. Please upgrade your account to access it.");
            navigate("/pricing");
        }
    }, [testNumber, navigate, isShowingResults]);

    // Consolidated CLB info function matching Paragon conversion
    const getReadingCLBInfo = (score) => {
        if (score >= 33) return { clbLevel: "10-12", clbNumeric: 10, feedback: "⭐⭐⭐⭐⭐" };
        if (score >= 31) return { clbLevel: "9", clbNumeric: 9, feedback: "😊" };
        if (score >= 28) return { clbLevel: "8", clbNumeric: 8, feedback: "😊" };
        if (score >= 24) return { clbLevel: "7", clbNumeric: 7, feedback: "😊" };
        if (score >= 19) return { clbLevel: "6", clbNumeric: 6, feedback: "😊" };
        if (score >= 15) return { clbLevel: "5", clbNumeric: 5, feedback: "You can do it. Keep practicing!" };
        if (score >= 10) return { clbLevel: "4", clbNumeric: 4, feedback: "You can do it. Keep practicing!" };
        if (score >= 8) return { clbLevel: "3", clbNumeric: 3, feedback: "You can do it. Keep practicing!" };
        return { clbLevel: "M", clbNumeric: 2, feedback: "You can do it. Keep practicing!" };
    };

    // ==================== saveReadingAttempt ====================
    const saveReadingAttempt = React.useCallback(async (attemptData) => {
        try {
            if (!attemptData.userId) return;

            const answersArray = attemptData.answers || [];
            const correctAnswers = answersArray.filter(ans => ans.isCorrect).length;
            const attemptScore = correctAnswers;
            const { clbNumeric: clbScore } = getReadingCLBInfo(attemptScore);

            const payload = { ...attemptData, attemptScore, clbScore };

            const response = await fetch(`${API_BASE}/api/reading-attempts`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            await response.json();
            if (!response.ok) toast.error("Failed to save attempt");
        } catch (err) {
            console.error("Error saving attempt:", err);
        }
    }, []);

    // ==================== finishTestAndGrade ====================
    const finishTestAndGrade = React.useCallback(async () => {
        let totalScore = 0;
        const taskResults = {};

        for (let tNum = 1; tNum <= 4; tNum++) {
            const taskData = data.find((item) => item.Task === tNum);
            const questionCount = TASKS[tNum]?.questions || 10;
            const answered = taskAnswers[tNum] || {};
            const answersArray = [];
            let taskScore = 0;

            for (let qNum = 1; qNum <= questionCount; qNum++) {
                const userAnswer = answered[`Q${qNum}`] || "";
                const correctAnswer = taskData?.[`Q${qNum}_Answer`] || "";
                const isCorrect = userAnswer !== "" && userAnswer === correctAnswer;
                if (isCorrect) taskScore++;

                answersArray.push({
                    questionId: taskData?.[`Q${qNum}_ID`] || `Q${qNum}`,
                    selectedAnswer: userAnswer,
                    correctAnswer,
                    isCorrect,
                });
            }

            totalScore += taskScore;
            taskResults[tNum] = {
                score: taskScore,
                answered,
                passage: taskData,
                answersArray,
            };

            if (currentUser?._id) {
                const attemptData = {
                    userId: currentUser._id,
                    testNumber,
                    taskNumber: tNum,
                    answers: answersArray,
                };
                await saveReadingAttempt(attemptData);
            }
        }

        setSubmittedTasks(taskResults);
        setScoreByTest((prev) => ({ ...prev, [testNumber]: totalScore }));
        setTestCompleted((prev) => ({ ...prev, [testNumber]: true }));

        if (typeof onComplete === 'function') {
            onComplete(totalScore, []);
        }
    }, [data, taskAnswers, currentUser, testNumber, saveReadingAttempt, onComplete]);

    // Timer logic per task (only ticks down while on active test)
    useEffect(() => {
        if (showInstructions || testCompleted[testNumber] || isShowingResults || !passage) {
            if (timerRef.current) clearInterval(timerRef.current);
            return;
        }

        const taskNumberLocal = taskIndex + 1;
        setTimeLeft(TASKS[taskNumberLocal].timeSec);

        if (timerRef.current) clearInterval(timerRef.current);

        timerRef.current = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    if (taskIndex < 3) {
                        setTaskIndex((p) => p + 1);
                    } else {
                        finishTestAndGrade();
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timerRef.current);
    }, [taskIndex, testNumber, passage, showInstructions, testCompleted, isShowingResults, finishTestAndGrade]);

    const handleAnswerChange = (qNum, val) => {
        const taskNum = taskIndex + 1;
        setTaskAnswers((prev) => ({
            ...prev,
            [taskNum]: {
                ...(prev[taskNum] || {}),
                [`Q${qNum}`]: val,
            },
        }));
    };

    // Format seconds to MM:SS
    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    };

    // Get options for a question number
    const getOptions = (qNum) => {
        const optionCount = passage?.Task === 3 ? 5 : 4;
        const options = [];
        for (let j = 1; j <= optionCount; j++) {
            const opt = passage[`Q${qNum}_Opt${j}`];
            if (opt) options.push(opt);
        }
        return options;
    };

    // Fetch test data — handles both normal flow and CheckProgress "View Results" flow
    useEffect(() => {
        const savedAttempts = location.state?.savedAttempts;

        if (isShowingResults && savedAttempts?.length > 0) {
            const taskQuestionCounts = [
                { taskNum: 1, numQ: TASKS[1].questions },
                { taskNum: 2, numQ: TASKS[2].questions },
                { taskNum: 3, numQ: TASKS[3].questions },
                { taskNum: 4, numQ: TASKS[4].questions },
            ];

            const reconstructed = {};
            let totalScore = 0;
            const remaining = [...savedAttempts];

            for (const { taskNum, numQ } of taskQuestionCounts) {
                let idx = remaining.findIndex((att) => Number(att.taskNumber) === taskNum);
                if (idx === -1) {
                    idx = remaining.findIndex((att) => att.answers?.length === numQ);
                }
                if (idx === -1) continue;

                const matchingAttempt = remaining.splice(idx, 1)[0];
                const syntheticPassage = { Task: taskNum };
                matchingAttempt.answers.forEach((ans, i) => {
                    syntheticPassage[`Q${i + 1}_Answer`] = ans.correctAnswer || '';
                });

                const answeredMap = {};
                let taskScore = 0;
                matchingAttempt.answers.forEach((ans, i) => {
                    answeredMap[`Q${i + 1}`] = ans.selectedAnswer || '';
                    if (ans.isCorrect) taskScore++;
                });

                totalScore += taskScore;
                reconstructed[taskNum] = {
                    score: taskScore,
                    answered: answeredMap,
                    passage: syntheticPassage,
                };
            }

            if (Object.keys(reconstructed).length > 0) {
                setSubmittedTasks(reconstructed);
                const loadedAnswers = {};
                Object.entries(reconstructed).forEach(([tNum, tData]) => {
                    loadedAnswers[tNum] = tData.answered || {};
                });
                setTaskAnswers(loadedAnswers);
                setScoreByTest((prev) => ({ ...prev, [testNumber]: totalScore }));
                setTestCompleted((prev) => ({ ...prev, [testNumber]: true }));
            }

            window.history.replaceState({}, document.title);

            fetch(`https://engdemyeducation.com/CelpipReading?testNumber=${testNumber}`)
                .then((res) => res.json())
                .then((filtered) => setData(filtered))
                .catch(() => {});
        } else {
            fetch(`https://engdemyeducation.com/CelpipReading?testNumber=${testNumber}`)
                .then((res) => res.json())
                .then((filtered) => {
                    setData(filtered);
                    setTaskAnswers({ 1: {}, 2: {}, 3: {}, 4: {} });
                    setPassage(null);
                    setTaskIndex(0);
                    setSubmittedTasks({});
                    setTestCompleted({});
                    setScoreByTest({});
                })
                .catch((err) => console.error("Error fetching reading data:", err));
        }
    }, [testNumber, location.state, isShowingResults]);

    // Set passage for current taskIndex (preserves user answers across tasks)
    useEffect(() => {
        const taskNumberLocal = taskIndex + 1;
        const taskData = data.find((item) => item.Task === taskNumberLocal);
        if (taskData) {
            setPassage(taskData);
        } else {
            setPassage(null);
        }
    }, [data, taskIndex]);

    const handleStartReading = () => {
        stopSpeech();
        setShowInstructions(false);
        setTimeLeft(TASKS[1].timeSec);
    };

    const handleGlobalNext = async () => {
        if (showInstructions) {
            handleStartReading();
            return;
        }

        if (taskIndex < 3) {
            setTaskIndex((prev) => prev + 1);
        } else {
            await finishTestAndGrade();
        }
    };

    const handleGlobalBack = () => {
        stopSpeech();
        if (testCompleted[testNumber] || isShowingResults) {
            navigate('/dashboard?section=reading');
        } else if (showInstructions) {
            navigate('/dashboard?section=reading');
        } else if (taskIndex > 0) {
            setTaskIndex((prev) => prev - 1);
        } else {
            setShowInstructions(true);
        }
    };

    // Helper function to render text with line breaks
    const renderTextWithLineBreaks = (text) => {
        if (!text) return null;
        const normalized = text
            .replace(/\\r\\n/g, '\n')
            .replace(/\\n/g, '\n')
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n');

        const parts = normalized.split('\n');
        return parts.map((part, index) => (
            <React.Fragment key={index}>
                {part}
                {index < parts.length - 1 && <br />}
            </React.Fragment>
        ));
    };

    // Inline dropdown field matching Paragon UI (never shows checked answers during the test)
    const renderAnswerField = (qNum) => {
        const taskNumberLocal = taskIndex + 1;
        const userAnswer = taskAnswers[taskNumberLocal]?.[`Q${qNum}`] || "";
        const options = getOptions(qNum);

        return (
            <select
                className="paragon-reading-inline-select"
                value={userAnswer}
                onChange={(e) => handleAnswerChange(qNum, e.target.value)}
            >
                <option value="">Select answer ▾</option>
                {options.map((opt, idx) => (
                    <option key={idx} value={opt}>
                        {opt}
                    </option>
                ))}
            </select>
        );
    };

    // Inline question with dropdown placed in blanks (showNumberNearOption is true only for inline paragraphs)
    const renderQuestionWithInlineSelect = (qNum, rawText, showNumberNearOption = false) => {
        if (!rawText) return null;
        let normalized = rawText
            .replace(/\\r\\n/g, '\n')
            .replace(/\\n/g, '\n')
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .trim();

        // Strip redundant (1) or (Q1) right after blanks if present
        normalized = normalized.replace(/_{2,}\s*\(\s*\d+\s*\)/g, '__________');

        const blankRegex = /_{2,}/;
        if (blankRegex.test(normalized)) {
            const parts = normalized.split(blankRegex);
            const prefix = parts[0].replace(/^[\r\n\s]+/, '').trimEnd();
            return (
                <span>
                    {renderTextWithLineBreaks(prefix)}{' '}
                    <span style={{ display: 'inline-flex', alignItems: 'center', margin: '0 4px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        {showNumberNearOption && (
                            <strong style={{ color: '#005a9c', fontWeight: 800, fontSize: 14.5, marginRight: 5 }}>({qNum})</strong>
                        )}
                        {renderAnswerField(qNum)}
                    </span>{' '}
                    {renderTextWithLineBreaks(parts.slice(1).join('').trimStart())}
                </span>
            );
        }
        return (
            <span>
                {renderTextWithLineBreaks(normalized.replace(/^[\r\n\s]+/, ''))}{' '}
                <span style={{ display: 'inline-flex', alignItems: 'center', margin: '0 4px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                    {showNumberNearOption && (
                        <strong style={{ color: '#005a9c', fontWeight: 800, fontSize: 14.5, marginRight: 5 }}>({qNum})</strong>
                    )}
                    {renderAnswerField(qNum)}
                </span>
            </span>
        );
    };

    // Paragraph with inline dropdowns for Task 1 (Q7 to Q11)
    const renderInlineParagraph = () => {
        if (!passage || passage.Task !== 1) return null;
        const questionsText = [];
        for (let qNum = 7; qNum <= 11; qNum++) {
            const questionText = passage[`Q${qNum}`];
            if (questionText) {
                // Strip redundant (Q7) / Q7 prefixes if present in API data
                const cleanText = questionText.replace(new RegExp(`^\\s*\\(?Q?${qNum}\\)?[:.]?\\s*`, 'i'), '');
                questionsText.push({ qNum, text: cleanText });
            }
        }
        return (
            <div style={{ lineHeight: 1.85, fontSize: 15, color: '#222222', margin: 0 }}>
                {questionsText.map(({ qNum, text }, i) => (
                    <React.Fragment key={qNum}>
                        {renderQuestionWithInlineSelect(qNum, text, true)}
                        {i !== questionsText.length - 1 && " "}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    // Paragraph with inline dropdowns for Task 2 (Q1 to Q5)
    const renderDiagramInlineParagraph = () => {
        if (!passage || passage.Task !== 2) return null;
        const questionsText = [];
        for (let qNum = 1; qNum <= 5; qNum++) {
            const questionText = passage[`Q${qNum}`];
            if (questionText) {
                const cleanText = questionText.replace(new RegExp(`^\\s*\\(?Q?${qNum}\\)?[:.]?\\s*`, 'i'), '');
                questionsText.push({ qNum, text: cleanText });
            }
        }
        return (
            <div style={{ lineHeight: 1.85, fontSize: 15, color: '#222222', margin: 0 }}>
                {questionsText.map(({ qNum, text }, i) => (
                    <React.Fragment key={qNum}>
                        {renderQuestionWithInlineSelect(qNum, text, true)}
                        {i !== questionsText.length - 1 && " "}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    // Paragraph with inline dropdowns for Task 4 (Q6 to Q10)
    const renderPart4InlineParagraph = () => {
        if (!passage || passage.Task !== 4) return null;
        const questionsText = [];
        for (let qNum = 6; qNum <= 10; qNum++) {
            const questionText = passage[`Q${qNum}`];
            if (questionText) {
                const cleanText = questionText.replace(new RegExp(`^\\s*\\(?Q?${qNum}\\)?[:.]?\\s*`, 'i'), '');
                questionsText.push({ qNum, text: cleanText });
            }
        }
        return (
            <div style={{ lineHeight: 1.85, fontSize: 15, color: '#222222', margin: 0 }}>
                {questionsText.map(({ qNum, text }, i) => (
                    <React.Fragment key={qNum}>
                        {renderQuestionWithInlineSelect(qNum, text, true)}
                        {i !== questionsText.length - 1 && " "}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    // Render Official Paragon Exam Top Header
    const renderExamHeader = () => (
        <div className="paragon-exam-header">
            <div className="paragon-exam-header-left">
                <img src={EngdemyLogo} alt="CELPIP" className="paragon-exam-logo" />
                <span className="paragon-exam-brand">CELPIP</span>
                <span className="paragon-exam-sep">|</span>
                <span className="paragon-exam-study-link" onClick={() => { stopSpeech(); navigate('/dashboard'); }}>
                    Study Materials
                </span>
            </div>
            <div className="paragon-exam-header-center">
                <span style={{ marginRight: 6 }}>Selected Product:</span>
                <select
                    value={testNumber}
                    onChange={(e) => {
                        stopSpeech();
                        const newTestNum = Number(e.target.value);
                        setTestNumber(newTestNum);
                        if (isFullTest || location.pathname.startsWith('/fulltest')) {
                            navigate(`/fulltest/${newTestNum}`);
                        } else {
                            navigate(`/reading/${newTestNum}`);
                        }
                    }}
                    className="paragon-header-select"
                >
                    {[...Array(50)].map((_, idx) => {
                        const num = idx + 1;
                        const label = num <= 2
                            ? `Free CELPIP-General Practice Test ${num}`
                            : `CELPIP-General Practice Test ${num}`;
                        return (
                            <option key={num} value={num}>
                                {label}
                            </option>
                        );
                    })}
                </select>
            </div>
            <div className="paragon-exam-header-right">
                <span className="paragon-exam-gear">⚙</span>
                <span className="paragon-exam-user-email">
                    {(currentUser?.email || currentUser?.name || "MY ACCOUNT").toUpperCase()} ▾
                </span>
            </div>
        </div>
    );

    // Render Official Paragon Subbar with NEXT and Timer
    const renderExamSubBar = () => {
        let subbarTitle = `Practice Test ${testNumber} - Reading`;
        if (!showInstructions && !testCompleted[testNumber]) {
            subbarTitle = `Practice Test ${testNumber} - Reading ${PART_LABELS[taskIndex + 1]}`;
        }

        return (
            <div className="paragon-exam-subbar">
                <div className="paragon-exam-subbar-title">
                    {subbarTitle}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    {!showInstructions && !testCompleted[testNumber] && (
                        <span style={{ fontSize: 13.5, color: '#333333', fontWeight: 500 }}>
                            Time remaining: <strong style={{ color: '#000000', fontWeight: 800 }}>{formatTime(timeLeft)}</strong>
                        </span>
                    )}
                    <button className="paragon-exam-next-btn" onClick={handleGlobalNext}>
                        {showInstructions ? "NEXT" : taskIndex === 3 ? "FINISH" : "NEXT"}
                    </button>
                </div>
            </div>
        );
    };

    // Render Official Paragon Bottom Bar with BACK
    const renderExamBottomBar = () => (
        <div className="paragon-exam-bottombar">
            <button className="paragon-exam-back-btn" onClick={handleGlobalBack}>
                BACK
            </button>
        </div>
    );

    // Render Official Footer
    const renderExamFooter = () => (
        <div className="paragon-exam-footer">
            Copyright 2026 - Engdemy Education Inc
        </div>
    );

    // Render Official Reading Instructions Screen
    const renderInstructionsPage = () => (
        <div className="paragon-instructions-wrapper">
            <div className="paragon-instructions-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <div className="paragon-instructions-header" style={{ marginBottom: 0 }}>
                        <span className="paragon-info-badge">i</span>
                        <h2 className="paragon-instructions-title" style={{ fontSize: 22, fontWeight: 800, color: '#000000' }}>
                            Reading Test Instructions
                        </h2>
                    </div>
                    <button
                        onClick={toggleSpeech}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            backgroundColor: isSpeaking ? '#005a9c' : '#ffffff',
                            color: isSpeaking ? '#ffffff' : '#000000',
                            border: '1.5px solid #000000',
                            borderRadius: 4,
                            padding: '7px 16px',
                            fontSize: 14,
                            fontWeight: 700,
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                        }}
                        title="Read instructions aloud"
                    >
                        <span>{isSpeaking ? '🔊 Reading Aloud...' : '▶ Listen Instructions'}</span>
                    </button>
                </div>

                <ul className="paragon-instructions-list">
                    {instructionBullets.map((bullet, idx) => (
                        <li key={idx} className="paragon-instruction-item">
                            <span
                                className="paragon-instruction-bullet"
                                style={{ backgroundColor: '#000000', width: 8, height: 8, marginTop: 9 }}
                            ></span>
                            <span
                                className="paragon-instruction-text"
                                style={{ fontSize: 17.5, color: '#000000', lineHeight: 1.6, fontWeight: 700 }}
                            >
                                {bullet}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );

    // Render Dual-Pane Reading Test Card
    const renderActiveTestContent = () => {
        if (!passage) {
            return (
                <div style={{ padding: 60, textAlign: 'center', color: '#666', width: '100%' }}>
                    <p style={{ fontSize: 18, fontWeight: 600 }}>Loading Reading Passage...</p>
                </div>
            );
        }

        return (
            <div className="paragon-reading-card">
                <div className="paragon-reading-split">
                    {/* LEFT PANE */}
                    <div className="paragon-reading-left">
                        <div className="paragon-reading-heading">
                            <span className="paragon-info-badge">i</span>
                            <h3 className="paragon-reading-heading-text">
                                {passage.Task === 1 ? "Read the following message." :
                                 passage.Task === 2 ? "Read the following diagram and message." :
                                 passage.Task === 3 ? "Read the following text." :
                                 "Read the following article."}
                            </h3>
                        </div>

                        <div className="paragon-reading-scroll-area">
                            <div className="paragon-reading-passage">
                                {passage.Task === 3 && passage.Passage ? (
                                    <>
                                        {passage.Passage.split(/(?=[A-D]\.)/g).map((part, idx) => {
                                            const match = part.match(/^([A-D])\.\s*(.*)/s);
                                            if (match) {
                                                return (
                                                    <p key={idx} style={{ marginBottom: 14, lineHeight: 1.65 }}>
                                                        <strong>{match[1]}.</strong> {renderTextWithLineBreaks(match[2])}
                                                    </p>
                                                );
                                            }
                                            return null;
                                        })}
                                        <p style={{ fontWeight: "bold", marginTop: 14, color: '#1a5276' }}>
                                            E. Not given in any of the above paragraphs.
                                        </p>
                                    </>
                                ) : (
                                    (passage.Passage || '')
                                        .replace(/Reading\s+for\s+viewpoints\s*-\s*Test\s*\d+/gi, 'Reading for Viewpoints')
                                        .replace(/\\r\\n/g, '\n')
                                        .replace(/\\n/g, '\n')
                                        .replace(/\r\n/g, '\n')
                                        .replace(/\r/g, '\n')
                                        .split(/\n\n|\n/)
                                        .map((p, i) => {
                                            const trimmed = p.trim();
                                            if (!trimmed) return null;
                                            const isViewpointsHeading = trimmed.toLowerCase() === 'reading for viewpoints';
                                            return (
                                                <p
                                                    key={i}
                                                    style={{
                                                        marginBottom: 14,
                                                        lineHeight: 1.65,
                                                        fontWeight: isViewpointsHeading ? 800 : 'normal',
                                                        fontSize: isViewpointsHeading ? 17 : 15,
                                                        color: isViewpointsHeading ? '#000000' : 'inherit'
                                                    }}
                                                >
                                                    {renderTextWithLineBreaks(trimmed)}
                                                </p>
                                            );
                                        })
                                )}

                                {passage.Task === 2 && passage.Image && (
                                    <img src={passage.Image} alt="Diagram" className="paragon-reading-diagram" />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT PANE */}
                    <div className="paragon-reading-right">
                        <div className="paragon-reading-heading">
                            <span className="paragon-info-badge">i</span>
                            <h3 className="paragon-reading-heading-text">
                                {passage.Task === 1
                                    ? "Using the drop-down menu ( ▾ ), choose the best option according to the information given in the message."
                                    : passage.Task === 2
                                    ? "Read the following email message about the diagram. Complete the email by filling in the blanks. Select the best choice for each blank from the drop-down menu ( ▾ )."
                                    : passage.Task === 3
                                    ? "Decide which paragraph, A to D, has the information given in each statement below. Select E if the information is not given in any of the paragraphs."
                                    : "Using the drop-down menu ( ▾ ), choose the best option according to the text."}
                            </h3>
                        </div>

                        {/* Task 1 Questions */}
                        {passage.Task === 1 && (
                            <>
                                <div className="paragon-reading-questions-box">
                                    {Array.from({ length: 6 }).map((_, i) => {
                                        const qNum = i + 1;
                                        const rawQText = passage[`Q${qNum}`];
                                        if (!rawQText) return null;
                                        const qText = rawQText
                                            .replace(/\\r\\n/g, '\n')
                                            .replace(/\\n/g, '\n')
                                            .replace(/\r\n/g, '\n')
                                            .replace(/\r/g, '\n')
                                            .trim()
                                            .replace(new RegExp(`^\\s*\\(?Q?${qNum}\\)?[:.]?\\s*`, 'i'), '');

                                        return (
                                            <div key={qNum} className="paragon-reading-q-item">
                                                <span style={{ fontWeight: 700, marginRight: 6 }}>{qNum}.</span>
                                                {renderQuestionWithInlineSelect(qNum, qText)}
                                            </div>
                                        );
                                    })}
                                </div>

                                <div style={{ marginTop: 22 }}>
                                    <div className="paragon-reading-heading" style={{ marginBottom: 12 }}>
                                        <span className="paragon-info-badge">i</span>
                                        <h4 className="paragon-reading-heading-text" style={{ fontSize: 15 }}>
                                            Here is a response to the message. Complete the response by filling in the blanks. Select the best choice for each blank from the drop-down menu ( ▾ ).
                                        </h4>
                                    </div>
                                    <div className="paragon-reading-questions-box">
                                        {renderInlineParagraph()}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Task 2 Questions */}
                        {passage.Task === 2 && (
                            <>
                                <div className="paragon-reading-questions-box" style={{ marginBottom: 20 }}>
                                    {renderDiagramInlineParagraph()}
                                </div>

                                <div className="paragon-reading-heading" style={{ marginBottom: 10 }}>
                                    <h4 className="paragon-reading-heading-text" style={{ fontSize: 15 }}>
                                        Using the drop-down menu ( ▾ ), choose the best option.
                                    </h4>
                                </div>
                                <div className="paragon-reading-questions-box">
                                    {Array.from({ length: 3 }).map((_, i) => {
                                        const qNum = i + 6;
                                        const qText = passage[`Q${qNum}`];
                                        if (!qText) return null;
                                        return (
                                            <div key={qNum} className="paragon-reading-q-item">
                                                <span style={{ fontWeight: 700, marginRight: 6 }}>{qNum}.</span>
                                                {renderQuestionWithInlineSelect(qNum, qText)}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {/* Task 3 Questions */}
                        {passage.Task === 3 && (
                            <div className="paragon-reading-questions-box">
                                {Array.from({ length: TASKS[3].questions }).map((_, i) => {
                                    const qNum = i + 1;
                                    const qText = passage[`Q${qNum}`];
                                    if (!qText) return null;
                                    return (
                                        <div key={qNum} className="paragon-reading-q-item">
                                            <span style={{ fontWeight: 700, marginRight: 6 }}>{qNum}.</span>
                                            {renderQuestionWithInlineSelect(qNum, qText)}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Task 4 Questions */}
                        {passage.Task === 4 && (
                            <>
                                <div className="paragon-reading-questions-box">
                                    {Array.from({ length: 5 }).map((_, i) => {
                                        const qNum = i + 1;
                                        const qText = passage[`Q${qNum}`];
                                        if (!qText) return null;
                                        return (
                                            <div key={qNum} className="paragon-reading-q-item">
                                                <span style={{ fontWeight: 700, marginRight: 6 }}>{qNum}.</span>
                                                {renderQuestionWithInlineSelect(qNum, qText)}
                                            </div>
                                        );
                                    })}
                                </div>

                                <div style={{ marginTop: 22 }}>
                                    <div className="paragon-reading-heading" style={{ marginBottom: 12 }}>
                                        <span className="paragon-info-badge">i</span>
                                        <h4 className="paragon-reading-heading-text" style={{ fontSize: 15 }}>
                                            Complete the following comment by selecting the best options for Q6 to Q10:
                                        </h4>
                                    </div>
                                    <div className="paragon-reading-questions-box">
                                        {renderPart4InlineParagraph()}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // If test is completed, render official Paragon score card & answer key
    if (testCompleted[testNumber] || isShowingResults) {
        const totalScore = scoreByTest[testNumber] || 0;
        const totalQuestions = 38;
        const { clbLevel } = getReadingCLBInfo(totalScore);

        return (
            <div className="paragon-exam-page-wrapper">
                {renderExamHeader()}
                <div className="paragon-exam-subbar">
                    <div className="paragon-exam-subbar-title">
                        Practice Test {testNumber} - Your Reading CELPIP Score
                    </div>
                    <button
                        className="paragon-exam-next-btn"
                        onClick={() => {
                            const el = document.getElementById('answer-key-section');
                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }}
                    >
                        NEXT
                    </button>
                </div>

                <div className="paragon-exam-body-content" style={{ padding: '24px 20px' }}>
                    <div className="paragon-score-container">
                        {/* 1. Official Paragon Score Table Card */}
                        <div className="paragon-score-table-card">
                            <div className="paragon-score-table-header-strip">
                                CELPIP-GENERAL READING TEST
                            </div>
                            <div className="paragon-score-cols-header">
                                <div className="paragon-score-col-th">Number of Questions</div>
                                <div className="paragon-score-col-th">Your Score</div>
                                <div className="paragon-score-col-th">Your Approximate CELPIP Score</div>
                            </div>
                            <div className="paragon-score-cols-values">
                                <div className="paragon-score-col-td">{totalQuestions}</div>
                                <div className="paragon-score-col-td">{totalScore}</div>
                                <div className="paragon-score-col-td">{clbLevel}</div>
                            </div>
                        </div>

                        {/* 2. Official Note Card */}
                        <div className="paragon-score-note-card">
                            <h3 className="paragon-score-note-title">A Note About Your CELPIP Score</h3>
                            <p className="paragon-score-note-text">
                                The score provided here is an estimate based on the Reading Test Score Conversion Chart. Each CELPIP test item is field tested and reviewed by an expert panel before it is integrated into the official test. Since questions may have different levels of difficulty and may therefore be equated differently, the raw score required for a certain level may vary slightly from one test to another.
                            </p>
                        </div>

                        {/* 3. Official Answer Key & Review Card */}
                        <div id="answer-key-section" className="paragon-score-answerkey-card">
                            <div className="paragon-score-table-header-strip" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>ANSWER KEY & TEST REVIEW</span>
                                <span style={{ fontSize: 13, fontWeight: 700 }}>Total Score: {totalScore} / {totalQuestions}</span>
                            </div>

                            {/* TABS FOR PARTS 1 TO 4 */}
                            <div className="paragon-score-tabs">
                                {[1, 2, 3, 4].map((partNum) => {
                                    const taskData = submittedTasks[partNum];
                                    const partScore = taskData?.score || 0;
                                    const partTotal = TASKS[partNum].questions;
                                    return (
                                        <button
                                            key={partNum}
                                            className={`paragon-score-tab-btn ${selectedReviewPart === partNum ? 'active' : ''}`}
                                            onClick={() => setSelectedReviewPart(partNum)}
                                        >
                                            Part {partNum} ({partScore}/{partTotal})
                                        </button>
                                    );
                                })}
                            </div>

                            {/* TAB CONTENT: REVIEW TABLE */}
                            <div className="paragon-score-tab-content">
                                {(() => {
                                    const taskData = submittedTasks[selectedReviewPart];
                                    const currentPassage = taskData?.passage || data.find((d) => d.Task === selectedReviewPart);
                                    const totalQ = TASKS[selectedReviewPart]?.questions || 10;
                                    const answeredMap = taskData?.answered || taskAnswers[selectedReviewPart] || {};

                                    return (
                                        <div>
                                            <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <h4 style={{ margin: 0, color: '#1a4971', fontSize: 16 }}>
                                                    {PART_LABELS[selectedReviewPart]}
                                                </h4>
                                                <span style={{ fontSize: 14, fontWeight: 700, color: '#005a9c' }}>
                                                    Score: {taskData?.score || 0} / {totalQ}
                                                </span>
                                            </div>

                                            <div style={{ overflowX: 'auto' }}>
                                                <table className="paragon-score-review-table">
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: '80px' }}>Question</th>
                                                            <th>Question Content</th>
                                                            <th style={{ width: '25%' }}>Your Answer</th>
                                                            <th style={{ width: '25%' }}>Correct Answer</th>
                                                            <th style={{ width: '90px', textAlign: 'center' }}>Result</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {Array.from({ length: totalQ }).map((_, idx) => {
                                                            const qNum = idx + 1;
                                                            const rawQText = currentPassage?.[`Q${qNum}`] || `Question ${qNum}`;
                                                            const cleanQText = rawQText
                                                                .replace(/\\r\\n/g, ' ')
                                                                .replace(/\\n/g, ' ')
                                                                .replace(/\r\n/g, ' ')
                                                                .replace(/\n/g, ' ')
                                                                .trim();
                                                            const correctAnswer = currentPassage?.[`Q${qNum}_Answer`] || 'N/A';
                                                            const userAnswer = answeredMap[`Q${qNum}`] || '';
                                                            const isCorrect = userAnswer !== '' && userAnswer === correctAnswer;

                                                            return (
                                                                <tr key={qNum}>
                                                                    <td style={{ fontWeight: 700, color: '#005a9c' }}>Q{qNum}</td>
                                                                    <td style={{ color: '#333', fontSize: 13.5 }}>{cleanQText}</td>
                                                                    <td>
                                                                        <span style={{
                                                                            fontWeight: 600,
                                                                            color: userAnswer ? (isCorrect ? '#2e7d32' : '#c62828') : '#888888'
                                                                        }}>
                                                                            {userAnswer || '(Not answered)'}
                                                                        </span>
                                                                    </td>
                                                                    <td style={{ fontWeight: 700, color: '#2e7d32' }}>
                                                                        ✓ {correctAnswer}
                                                                    </td>
                                                                    <td style={{ textAlign: 'center' }}>
                                                                        {isCorrect ? (
                                                                            <span className="paragon-badge-correct">Correct</span>
                                                                        ) : (
                                                                            <span className="paragon-badge-incorrect">Incorrect</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* 4. Action Buttons */}
                        <div className="paragon-score-actions">
                            <button
                                className="paragon-score-btn paragon-score-btn-primary"
                                onClick={() => {
                                    setSubmittedTasks({});
                                    setTestCompleted({});
                                    setScoreByTest({});
                                    setTaskIndex(0);
                                    setTaskAnswers({ 1: {}, 2: {}, 3: {}, 4: {} });
                                    setShowInstructions(true);
                                    setTimeLeft(TASKS[1].timeSec);
                                }}
                            >
                                Retake Test {testNumber}
                            </button>
                            <button
                                className="paragon-score-btn paragon-score-btn-secondary"
                                onClick={() => navigate('/dashboard?section=reading')}
                            >
                                All Reading Tests
                            </button>
                            <button
                                className="paragon-score-btn paragon-score-btn-secondary"
                                onClick={() => navigate('/dashboard')}
                            >
                                Dashboard
                            </button>
                        </div>
                    </div>
                </div>

                <div className="paragon-exam-bottombar">
                    <button className="paragon-exam-back-btn" onClick={() => navigate('/dashboard?section=reading')}>
                        BACK
                    </button>
                </div>
                {renderExamFooter()}
            </div>
        );
    }

    // Default Exam Flow: Instructions screen or Active dual-pane test
    return (
        <div className="paragon-exam-page-wrapper">
            {renderExamHeader()}
            {renderExamSubBar()}

            <div className="paragon-exam-body-content">
                {showInstructions ? (
                    renderInstructionsPage()
                ) : (
                    renderActiveTestContent()
                )}
            </div>

            {renderExamBottomBar()}
            {renderExamFooter()}
        </div>
    );
}

export default ReadingTest;